import * as R from 'ramda';

const toNumber = (value) => {
  if (R.isNil(value) || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const getQuestionIndex = (question) =>
  toNumber(
    R.prop('index', question) ??
      R.prop('questionIndex', question) ??
      R.prop('question_index', question)
  );

export const getQuizQuestion = (quiz, questionIndex) =>
  R.find(
    (question) => getQuestionIndex(question) === questionIndex,
    R.propOr([], 'questions', quiz)
  ) || R.nth(questionIndex, R.propOr([], 'questions', quiz));

export const getSubmissionQuestion = (submission, questionIndex) =>
  R.find(
    (question) => getQuestionIndex(question) === questionIndex,
    R.propOr([], 'questions', submission)
  ) || R.nth(questionIndex, R.propOr([], 'questions', submission));

const getLegacyAnswerQuestionIndex = (answer) =>
  toNumber(
    R.prop('questionIndex', answer) ??
      R.prop('question_index', answer) ??
      R.prop('index', answer)
  );

export const isMultipleChoiceQuestion = (question) =>
  R.prop('type', question) === 'multiple_choice';

export const getQuestionScore = (submission, questionIndex) => {
  const submissionQuestion = getSubmissionQuestion(submission, questionIndex);
  if (submissionQuestion) {
    return toNumber(
      R.prop('score', submissionQuestion) ??
        R.path(['answer', 'score'], submissionQuestion)
    );
  }

  const questionScores = R.prop('questionScores', submission);
  if (Array.isArray(questionScores)) {
    return toNumber(R.nth(questionIndex, questionScores));
  }

  const answers = R.propOr([], 'answers', submission);
  const answer = R.find(
    (item) => getLegacyAnswerQuestionIndex(item) === questionIndex,
    answers
  );
  if (!answer) return null;

  return toNumber(
    R.prop('score', answer) ??
      R.prop('questionScore', answer) ??
      R.prop('question_score', answer)
  );
};

export const getQuestionCount = (quiz) => {
  const questionCount = R.path(['questions', 'length'], quiz);
  if (questionCount) return questionCount;

  const submissions = R.propOr([], 'submissions', quiz);
  return R.pipe(
    R.chain((submission) => {
      const submissionQuestions = R.propOr([], 'questions', submission);
      if (!R.isEmpty(submissionQuestions)) {
        return R.map(getQuestionIndex, submissionQuestions);
      }

      const questionScores = R.prop('questionScores', submission);
      if (Array.isArray(questionScores)) {
        return R.range(0, questionScores.length);
      }

      return R.pipe(
        R.propOr([], 'answers'),
        R.map(getLegacyAnswerQuestionIndex),
        R.reject(R.isNil)
      )(submission);
    }),
    R.reject(R.isNil),
    R.reduce(R.max, -1),
    (maxIndex) => (maxIndex >= 0 ? maxIndex + 1 : 0)
  )(submissions);
};

export const getQuestionAverages = (quiz) => {
  const submissions = R.propOr([], 'submissions', quiz);
  const questionCount = getQuestionCount(quiz);
  if (!questionCount) return [];

  return R.range(0, questionCount).map((questionIndex) => {
    const scores = R.pipe(
      R.map((submission) => getQuestionScore(submission, questionIndex)),
      R.reject(R.isNil)
    )(submissions);

    return R.isEmpty(scores) ? null : R.mean(scores);
  });
};

export const getWeakestQuestion = (quiz) => {
  const averages = getQuestionAverages(quiz);

  return R.pipe(
    R.addIndex(R.reduce)((weakest, average, index) => {
      if (R.isNil(average)) return weakest;
      if (
        R.isNil(weakest) ||
        average < weakest.average ||
        (average === weakest.average && index < weakest.index)
      ) {
        return { index, average };
      }
      return weakest;
    }, null),
    R.when(R.is(Object), (weakest) => ({
      ...weakest,
      average: Math.round(weakest.average),
    }))
  )(averages);
};

export const formatWeakestQuestionLabel = (weakestQuestion) => {
  if (!weakestQuestion) return null;
  return `Q${weakestQuestion.index + 1} · ${weakestQuestion.average}% correct`;
};

export const getQuestionOptions = (question) => {
  const options = R.propOr([], 'options', question);
  if (!R.isEmpty(options)) return options;

  return R.propOr([], 'choices', question);
};

export const getOptionLabel = (option, index) => {
  if (typeof option === 'string') return option;
  return (
    R.prop('text', option) ||
    R.prop('label', option) ||
    R.prop('value', option) ||
    `Option ${index + 1}`
  );
};

export const getOptionRationale = (question, option, optionIndex) => {
  const explanations = R.propOr([], 'explanations', question);
  const explanation = R.nth(optionIndex, explanations);
  if (explanation) return explanation;

  if (typeof option === 'string') return null;
  return (
    R.prop('rationale', option) ||
    R.prop('explanation', option) ||
    R.prop('feedback', option)
  );
};

export const formatQuestionType = (type) =>
  String(type || '')
    .replace(/_/g, ' ')
    .trim();

export const getStudentChoice = (submissionQuestion) =>
  toNumber(
    R.path(['answer', 'studentChoice'], submissionQuestion) ??
      R.path(['answer', 'student_choice'], submissionQuestion)
  );

export const getStudentChoices = (submissionQuestion) => {
  const choices =
    R.path(['answer', 'studentChoices'], submissionQuestion) ||
    R.path(['answer', 'student_choices'], submissionQuestion);

  if (Array.isArray(choices)) {
    return R.pipe(R.map(toNumber), R.reject(R.isNil))(choices);
  }

  const singleChoice = getStudentChoice(submissionQuestion);
  return R.isNil(singleChoice) ? [] : [singleChoice];
};

export const getCorrectChoice = (question) =>
  toNumber(
    R.path(['solution', 'correctChoice'], question) ??
      R.path(['solution', 'correct_choice'], question)
  );

export const getCorrectChoices = (question) => {
  const choices =
    R.path(['solution', 'correctChoices'], question) ??
    R.path(['solution', 'correct_choices'], question) ??
    [];

  return R.pipe(R.map(toNumber), R.reject(R.isNil))(choices);
};

export const isOptionSelected = (question, optionIndex) =>
  R.includes(optionIndex, getStudentChoices(question));

export const isOptionCorrectAnswer = (question, optionIndex) => {
  if (isMultipleChoiceQuestion(question)) {
    return R.includes(optionIndex, getCorrectChoices(question));
  }

  const correctChoice = getCorrectChoice(question);
  if (R.isNil(correctChoice)) return null;
  return optionIndex === correctChoice;
};

export const isChoiceCorrect = (question, choiceIndex) =>
  isOptionCorrectAnswer(question, choiceIndex);

const mergeQuestionWithAnswer = (question, answerRecord = {}) => {
  const questionIndex =
    getQuestionIndex(question) ??
    getQuestionIndex(answerRecord) ??
    getLegacyAnswerQuestionIndex(answerRecord);
  const answer = R.prop('answer', answerRecord);

  return {
    ...question,
    index: questionIndex,
    ...(answer ? { answer } : {}),
    score: toNumber(
      R.prop('score', question) ??
        R.prop('score', answerRecord) ??
        R.path(['answer', 'score'], answerRecord) ??
        R.path(['answer', 'score'], question)
    ),
  };
};

export const buildSubmissionQuestions = (quiz, submission) => {
  const quizQuestions = R.propOr([], 'questions', quiz);
  const submissionQuestions = R.propOr([], 'questions', submission);

  if (!R.isEmpty(submissionQuestions)) {
    return submissionQuestions.map((submissionQuestion, arrayIndex) => {
      const questionIndex = getQuestionIndex(submissionQuestion) ?? arrayIndex;
      const quizQuestion = getQuizQuestion(quiz, questionIndex) || {};

      return mergeQuestionWithAnswer(
        {
          ...quizQuestion,
          ...submissionQuestion,
          explanations:
            submissionQuestion.explanations || quizQuestion.explanations,
          index: questionIndex,
        },
        submissionQuestion
      );
    });
  }

  if (!R.isEmpty(quizQuestions)) {
    return quizQuestions.map((quizQuestion, arrayIndex) => {
      const questionIndex = getQuestionIndex(quizQuestion) ?? arrayIndex;
      return mergeQuestionWithAnswer(
        { ...quizQuestion, index: questionIndex },
        {}
      );
    });
  }

  const questionScores = R.prop('questionScores', submission);
  if (Array.isArray(questionScores)) {
    return questionScores.map((score, index) => ({
      index,
      title: `Question ${index + 1}`,
      score: toNumber(score),
      options: [],
    }));
  }

  return [];
};
